import { computed } from 'vue';
import { useTenant } from './useTenant';
import type { UserRole } from '@/types/models';

/**
 * Role-Based Access Control (RBAC) composable
 * Conforms to AI_RULES.md RBAC specification
 * Roles: ADMIN, COACH, PLAYER, GUARDIAN
 */
export function useAuth() {
  const { currentUser, activeTenant } = useTenant();

  // Normalize role (maps legacy aliases to standard 4 roles)
  const currentRole = computed<UserRole>(() => {
    const raw = (currentUser.value?.role || 'ADMIN') as string;
    if (raw === 'ORG_ADMIN' || raw === 'TEAM_ADMIN' || raw === 'ADMIN') return 'ADMIN';
    if (raw === 'HEAD_COACH' || raw === 'ASSISTANT_COACH' || raw === 'COACH') return 'COACH';
    if (raw === 'PLAYER') return 'PLAYER';
    if (raw === 'PARENT' || raw === 'GUARDIAN') return 'GUARDIAN';
    return 'ADMIN';
  });

  const isAdmin = computed(() => currentRole.value === 'ADMIN');
  const isCoach = computed(() => currentRole.value === 'COACH');
  const isPlayer = computed(() => currentRole.value === 'PLAYER');
  const isGuardian = computed(() => currentRole.value === 'GUARDIAN');

  // RBAC Permission checks
  const canManageTenantSettings = computed(() => isAdmin.value);
  const canManageMembers = computed(() => isAdmin.value);
  const canInviteMembers = computed(() => isAdmin.value);
  const canManageSubscriptions = computed(() => isAdmin.value);
  const canChangePaymentMethod = computed(() => isAdmin.value);
  const canUpgradePlan = computed(() => isAdmin.value);

  // Both ADMIN and COACH have access to rosters, lineups, events, and seat allocations
  const canAccessRoster = computed(() => isAdmin.value || isCoach.value);
  const canAccessLineups = computed(() => isAdmin.value || isCoach.value);
  const canAccessEvents = computed(() => true); // All roles have schedule access
  const canAllocateSeats = computed(() => isAdmin.value || isCoach.value);

  // Individual dues / subscriptions for players and guardians
  const canAccessIndividualBilling = computed(() => isPlayer.value || isGuardian.value || isAdmin.value);

  return {
    currentUser,
    activeTenant,
    currentRole,
    isAdmin,
    isCoach,
    isPlayer,
    isGuardian,
    canManageTenantSettings,
    canManageMembers,
    canInviteMembers,
    canManageSubscriptions,
    canChangePaymentMethod,
    canUpgradePlan,
    canAccessRoster,
    canAccessLineups,
    canAccessEvents,
    canAllocateSeats,
    canAccessIndividualBilling,
  };
}
